import { Link } from "react-router-dom";
import { ArrowRight, Compass, Gem, Handshake, Quote } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES, TECH_STACK } from "@/data/site";

const Hero = () => (
  <section className="blueprint-grid" data-testid="about-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-14">
      <Overline>About us</Overline>
      <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl">
        <MaskedLine delay={0.15}>Built by engineers who</MaskedLine>
        <MaskedLine delay={0.3}>
          have <span className="text-cobalt italic">run the workloads.</span>
        </MaskedLine>
      </h1>
      <Reveal delay={0.45}>
        <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed">
          Tranzlytics Services &amp; Technology exists to make enterprise modernisation practical: data platforms that cost less to run, networks that stay up, and hardware that arrives on time.
        </p>
      </Reveal>
    </div>
  </section>
);

const Mission = () => (
  <section className="py-16 sm:py-20" data-testid="about-mission">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <Overline>Our mission</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
          Modernise the data. Power the workplace.
        </h2>
        <p className="mt-5 text-gray-600 leading-relaxed">
          Most enterprises run two battles at once: legacy data platforms draining budget, and office infrastructure procured from a dozen vendors. We take ownership of both — migrating analytics estates to Snowflake and Databricks while delivering the networking and hardware the business runs on.
        </p>
        <p className="mt-4 text-gray-600 leading-relaxed">
          Based in Noida, Delhi NCR, we serve clients across India with the same standard: senior people, honest estimates, and support that continues after go-live.
        </p>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="rounded-3xl overflow-hidden border border-gray-200 shadow-lg shadow-gray-200/60">
          <img src={IMAGES.team} alt="Tranzlytics modern workspace" className="w-full h-[360px] object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Stack = () => (
  <section className="py-20 bg-ink text-white relative grain overflow-hidden" data-testid="about-stack">
    <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline light>Technology stack</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">
          The modern platforms we deliver on.
        </h2>
        <p className="mt-5 text-white/60 leading-relaxed max-w-2xl">
          From cloud data warehouses and lakehouses to streaming, orchestration and applied AI — our engineers work hands-on across the full modern data estate.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="about-stack-badges">
          {TECH_STACK.map((t) => (
            <span key={t} className="bg-white/[0.07] border border-white/10 rounded-full px-4 py-2 text-sm text-white/80 hover:border-cobalt hover:text-white transition-colors duration-200">
              {t}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Values = () => (
  <section className="py-20 sm:py-24" data-testid="about-values">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>How we work</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink max-w-2xl">
          Values that survive go-live day.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-3 gap-5">
        {[
          { icon: Compass, t: "Senior-first delivery", d: "The people who scope your engagement are the people who deliver it — no hand-downs." },
          { icon: Gem, t: "Quality as a gate", d: "Reconciliation, testing and governance checks are part of the plan, not an afterthought." },
          { icon: Handshake, t: "One accountable partner", d: "Data, network and hardware under one roof means no vendor finger-pointing." },
        ].map((v, i) => (
          <Reveal key={v.t} delay={i * 0.1}>
            <div className="rounded-3xl bg-white border border-gray-200 p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gray-200" data-testid={`value-card-${i}`}>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-cobalt-light">
                <v.icon className="h-5 w-5 text-cobalt" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-ink">{v.t}</h3>
              <p className="mt-2.5 text-sm text-gray-600 leading-relaxed">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TESTIMONIALS = [
  { quote: "The SAS-to-Snowflake migration finished ahead of schedule, and our month-end processing cost dropped by more than half.", name: "VP, Analytics", org: "Banking & financial services client" },
  { quote: "One call handled our new office — networking, laptops, printers and the boardroom panel. Installation was spotless.", name: "Head of Administration", org: "Professional services firm, NCR" },
  { quote: "Their batch monitoring and governance automation removed the audit findings we had carried for years.", name: "Director, Data Operations", org: "Insurance sector client" },
];

const Testimonials = () => (
  <section className="pb-20 sm:pb-24" data-testid="about-testimonials">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>Client voices</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink max-w-2xl">
          What working with us feels like.
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <figure className="rounded-3xl bg-white border border-gray-200 p-7 h-full flex flex-col" data-testid={`testimonial-card-${i}`}>
              <Quote className="h-6 w-6 text-cobalt" />
              <blockquote className="mt-4 text-sm sm:text-base text-gray-700 leading-relaxed flex-1">“{t.quote}”</blockquote>
              <figcaption className="mt-6">
                <p className="font-display font-bold text-ink text-sm">{t.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.org}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-14 rounded-[2rem] bg-cobalt text-white p-10 sm:p-12 flex flex-wrap items-center justify-between gap-6 relative grain overflow-hidden">
          <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
          <h3 className="relative font-display text-2xl sm:text-3xl font-extrabold tracking-tight max-w-xl">
            Let's write the next case study together.
          </h3>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="relative inline-flex items-center gap-2 bg-white text-cobalt font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-ink hover:text-white transition-colors duration-300"
          >
            Start a conversation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <Hero />
      <Marquee />
      <Mission />
      <Stack />
      <Values />
      <Testimonials />
    </div>
  );
}
